/**
 * THE single source of style truth. (brainstorm.md 1.10)
 *
 * Every color, radius, spacing step, font size, elevation, and motion curve in
 * C-GC is defined here and nowhere else. `eslint-rules/no-hardcoded-style-values.js`
 * fails the build on hex colors, raw `px` strings, `cubic-bezier(...)`, and raw
 * animation timings found anywhere outside this directory.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Classroom's live UI is mostly Material 2 with Material 3 creeping in, and we
 * could not verify which by looking at the real thing. Rather than guess and
 * bet the codebase on the answer, the question is dissolved: switching the whole
 * app from M2 to M3 is an edit to this file, not a rewrite. If the morning
 * screenshot review says "the corners are too sharp," change `radius` here and
 * the entire app follows.
 *
 * The meta-lesson from brainstorm.md 1.10, worth keeping: when a design question
 * is genuinely uncertain, make it cheap to change rather than guess harder.
 *
 * CURRENT TARGET: Material 2.
 * 4px base radii, tight density, M2 elevation shadows, Roboto, Google Blue 600
 * (#1a73e8) as the accent. M2 is a *frozen* spec while M3 is still moving
 * (M3 Expressive), so building against it means the design will not drift
 * underneath us.
 *
 * Roboto only. Google Sans and Product Sans are not freely licensed
 * (brainstorm.md 1.7).
 */

/* ------------------------------------------------------------------ */
/* Palette                                                             */
/* ------------------------------------------------------------------ */

/**
 * Raw Google/Material 2 palette values.
 *
 * These are the upstream Material swatches Google itself uses — Material is
 * Apache-2.0 and explicitly meant for anyone to build on, which is the safe
 * harbour described in brainstorm.md open question 4.5. Nothing here is a
 * Google logo, mark, or proprietary font.
 */
const ref = {
  blue600: '#1a73e8',
  blue700: '#1967d2',
  blue800: '#185abc',
  blue300: '#8ab4f8',
  blue200: '#aecbfa',
  blue50: '#e8f0fe',

  green700: '#137333',
  green600: '#1e8e3e',
  green300: '#81c995',
  green50: '#e6f4ea',

  red700: '#c5221f',
  red600: '#d93025',
  red300: '#f28b82',
  red50: '#fce8e6',

  yellow800: '#ea8600',
  yellow700: '#f29900',
  yellow300: '#fdd663',
  yellow50: '#fef7e0',

  purple600: '#a142f4',
  teal700: '#00796b',

  grey900: '#202124',
  grey800: '#3c4043',
  grey700: '#5f6368',
  grey600: '#80868b',
  grey500: '#9aa0a6',
  grey400: '#bdc1c6',
  grey300: '#dadce0',
  grey200: '#e8eaed',
  grey100: '#f1f3f4',
  grey50: '#f8f9fa',

  white: '#ffffff',
  black: '#000000',

  /** Material dark-theme surfaces, elevation-tinted. */
  darkSurface0: '#202124',
  darkSurface1: '#292a2d',
  darkSurface2: '#303134',
  darkSurface3: '#35363a',
} as const;

/** Semantic color roles for one theme mode. Light and dark supply the same keys. */
export interface ColorScheme {
  /** Brand accent. Classroom's signature is Google Blue 600. */
  accent: string;
  accentHover: string;
  accentPressed: string;
  /** Text/icon color that sits on top of `accent`. */
  onAccent: string;
  /** Very light accent wash — selected nav item, chip backgrounds. */
  accentSubtle: string;

  /** App background behind cards and content. */
  background: string;
  /** Card, sheet, dialog, and app-bar surfaces. */
  surface: string;
  /** A surface raised one step above `surface` (menus, hovered rows). */
  surfaceVariant: string;
  /** Background for the shell chrome (top bar / drawer). */
  chrome: string;

  textPrimary: string;
  textSecondary: string;
  textDisabled: string;
  /** Text/icon color on top of `accent`-filled course card headers. */
  textOnColor: string;

  divider: string;
  /** Slightly stronger divider for structural borders. */
  border: string;

  success: string;
  successSubtle: string;
  warning: string;
  warningSubtle: string;
  error: string;
  errorSubtle: string;
  info: string;
  infoSubtle: string;

  /** Overlay behind modals and the mobile drawer. */
  scrim: string;
  /** Skeleton loading block. */
  skeleton: string;

  /**
   * The single static course-card accent (brainstorm.md 1.12). Explicitly a
   * placeholder: deterministic-from-course-ID and manual per-course assignment
   * both remain open options in brainstorm.md section 2.
   */
  courseCardHeader: string;

  /**
   * Palette for telling courses apart *outside* the course cards — the small
   * label on a stream card, a calendar chip, a course dot.
   *
   * Deliberately separate from `courseCardHeader`, which 1.12 pins to one
   * static color. A stream mixing five classes is the one place where
   * distinguishing them at a glance genuinely pays, and this is the
   * "deterministic from course ID" option that 1.12 left open.
   *
   * NO RED. Red means missing/overdue everywhere else in this app, and a course
   * whose label is the same red as its own late work reads as a warning about
   * the course. Every entry here is a Material hue that clears text contrast on
   * this mode's surface.
   */
  courseAccents: readonly string[];
}

export const lightColors: ColorScheme = {
  accent: ref.blue600,
  accentHover: ref.blue700,
  accentPressed: ref.blue800,
  onAccent: ref.white,
  accentSubtle: ref.blue50,

  background: ref.grey50,
  surface: ref.white,
  surfaceVariant: ref.grey100,
  chrome: ref.white,

  textPrimary: ref.grey900,
  textSecondary: ref.grey700,
  textDisabled: ref.grey600,
  textOnColor: ref.white,

  divider: ref.grey300,
  border: ref.grey300,

  success: ref.green700,
  successSubtle: ref.green50,
  warning: ref.yellow800,
  warningSubtle: ref.yellow50,
  error: ref.red700,
  errorSubtle: ref.red50,
  info: ref.blue600,
  infoSubtle: ref.blue50,

  scrim: 'rgba(32, 33, 36, 0.6)',
  skeleton: ref.grey200,

  courseCardHeader: ref.blue600,

  courseAccents: [ref.blue600, ref.green600, ref.purple600, ref.teal700, ref.yellow800],
};

/**
 * Dark mode is wired through tokens from the start rather than retrofitted.
 * Retrofitting dark mode after the fact means auditing every component for
 * hardcoded light-mode assumptions, which is exactly the work this file exists
 * to avoid.
 */
export const darkColors: ColorScheme = {
  accent: ref.blue300,
  accentHover: ref.blue200,
  accentPressed: ref.blue200,
  onAccent: ref.grey900,
  accentSubtle: 'rgba(138, 180, 248, 0.16)',

  background: ref.darkSurface0,
  surface: ref.darkSurface1,
  surfaceVariant: ref.darkSurface2,
  chrome: ref.darkSurface1,

  textPrimary: ref.grey200,
  textSecondary: ref.grey500,
  textDisabled: ref.grey700,
  textOnColor: ref.grey900,

  divider: ref.grey800,
  border: ref.grey800,

  success: ref.green300,
  successSubtle: 'rgba(129, 201, 149, 0.16)',
  warning: ref.yellow300,
  warningSubtle: 'rgba(253, 214, 99, 0.16)',
  error: ref.red300,
  errorSubtle: 'rgba(242, 139, 130, 0.16)',
  info: ref.blue300,
  infoSubtle: 'rgba(138, 180, 248, 0.16)',

  scrim: 'rgba(0, 0, 0, 0.7)',
  skeleton: ref.darkSurface3,

  courseCardHeader: '#3c5a8a',

  // Lighter tints: the 600-weight hues used in light mode are close to
  // unreadable against a #202124 surface.
  courseAccents: [ref.blue300, ref.green300, '#d7aefb', '#78d9ec', ref.yellow300],
};

/* ------------------------------------------------------------------ */
/* Geometry                                                            */
/* ------------------------------------------------------------------ */

/**
 * Corner radii, in px.
 *
 * M2 values. Flip these to 12/16/28 and the app reads as Material 3 — that
 * single swap is the entire point of this file.
 */
export const radius = {
  none: 0,
  /** Default for buttons, inputs, chips, menus. */
  sm: 4,
  /** Cards. Classroom's course cards are noticeably rounder than its buttons. */
  md: 8,
  /** Dialogs and bottom sheets. */
  lg: 12,
  /** Fully rounded — pills, avatars, FABs. */
  pill: 9999,
} as const;

/** 4px base grid. `space(n)` is `n * 4` px, matching MUI's default spacing unit. */
export const spacingUnit = 4;

export const space = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
} as const;

/** Fixed sizes for shell chrome and recurring UI blocks. */
export const size = {
  appBarHeight: 64,
  appBarHeightMobile: 56,
  drawerWidth: 280,
  drawerWidthCollapsed: 72,
  courseCardHeaderHeight: 100,
  courseCardMinHeight: 260,
  avatarSm: 24,
  avatarMd: 32,
  avatarLg: 40,
  avatarXl: 96,
  iconSm: 18,
  iconMd: 24,
  iconLg: 32,
  /** Max width of a reading column (assignment description, announcements). */
  contentMaxWidth: 1080,
  /** Max width of the narrow single-column pages (stream, to-do). */
  narrowMaxWidth: 800,
  borderWidth: 1,
  focusRingWidth: 2,
} as const;

export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
} as const;

export const zIndex = {
  drawer: 1200,
  appBar: 1100,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
} as const;

/* ------------------------------------------------------------------ */
/* Typography                                                          */
/* ------------------------------------------------------------------ */

export const fontFamily = {
  /** Roboto only — Google Sans / Product Sans are not licensed (1.7). */
  base: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
  mono: '"Roboto Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
} as const;

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700,
} as const;

/** Font sizes in px. Converted to rem by the theme so they respect user zoom. */
export const fontSize = {
  xs: 11,
  sm: 12,
  base: 14,
  md: 16,
  lg: 18,
  xl: 22,
  xxl: 28,
  xxxl: 36,
  display: 48,
} as const;

export const lineHeight = {
  tight: 1.2,
  snug: 1.35,
  normal: 1.5,
  relaxed: 1.65,
} as const;

export const letterSpacing = {
  tight: '-0.02em',
  normal: '0',
  wide: '0.01em',
  wider: '0.05em',
} as const;

/* ------------------------------------------------------------------ */
/* Elevation                                                           */
/* ------------------------------------------------------------------ */

/**
 * Material 2 elevation shadows.
 *
 * M2 layers three shadows (umbra, penumbra, ambient) per level. M3 replaces
 * these with tonal surface tints, so this is one of the values that changes
 * character entirely on an M2 -> M3 swap.
 */
export const shadow = {
  none: 'none',
  /** Resting card. Classroom cards use a border plus a whisper of shadow. */
  sm: '0 1px 2px 0 rgba(60,64,67,0.30), 0 1px 3px 1px rgba(60,64,67,0.15)',
  /** Hovered card, raised button. */
  md: '0 1px 3px 0 rgba(60,64,67,0.30), 0 4px 8px 3px rgba(60,64,67,0.15)',
  /** Menus, popovers. */
  lg: '0 2px 6px 2px rgba(60,64,67,0.15), 0 1px 2px 0 rgba(60,64,67,0.30)',
  /** Dialogs. */
  xl: '0 4px 8px 3px rgba(60,64,67,0.15), 0 1px 3px 0 rgba(60,64,67,0.30)',
  /** Drawer / modal sheet. */
  xxl: '0 8px 12px 6px rgba(60,64,67,0.15), 0 4px 4px 0 rgba(60,64,67,0.30)',
} as const;

/** Dark mode needs heavier shadows to read at all against a dark background. */
export const shadowDark = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0,0,0,0.60), 0 1px 3px 1px rgba(0,0,0,0.35)',
  md: '0 1px 3px 0 rgba(0,0,0,0.60), 0 4px 8px 3px rgba(0,0,0,0.35)',
  lg: '0 2px 6px 2px rgba(0,0,0,0.35), 0 1px 2px 0 rgba(0,0,0,0.60)',
  xl: '0 4px 8px 3px rgba(0,0,0,0.35), 0 1px 3px 0 rgba(0,0,0,0.60)',
  xxl: '0 8px 12px 6px rgba(0,0,0,0.35), 0 4px 4px 0 rgba(0,0,0,0.60)',
} as const;

/* ------------------------------------------------------------------ */
/* Motion                                                              */
/* ------------------------------------------------------------------ */

/**
 * Animation quality was the deciding factor in picking Motion over MUI's own
 * transitions (brainstorm.md 1.11), so the motion tokens matter as much as the
 * color ones.
 *
 * Durations are given twice because the two consumers disagree on units: CSS
 * and MUI want milliseconds, Motion wants seconds. Deriving one from the other
 * keeps them from drifting apart.
 */
const durationMs = {
  /** Instant feedback — ripples, hover tints. */
  instant: 75,
  fast: 100,
  /** The default. Drawer slide, route fade, card hover lift. */
  medium: 200,
  slow: 300,
  /** Large-surface transitions — full-screen dialogs. */
  slower: 400,
} as const;

type DurationKey = keyof typeof durationMs;

const durationSeconds = Object.fromEntries(
  Object.entries(durationMs).map(([key, ms]) => [key, ms / 1000]),
) as Record<DurationKey, number>;

/** Material 2 easing curves, as CSS strings and as Motion control-point tuples. */
const easingPoints = {
  /** Standard: most transitions, both entering and leaving. */
  standard: [0.4, 0, 0.2, 1],
  /** Decelerate: elements entering the screen. */
  decelerate: [0, 0, 0.2, 1],
  /** Accelerate: elements leaving the screen. */
  accelerate: [0.4, 0, 1, 1],
  /** Sharp: elements that may return, e.g. a dismissed drawer. */
  sharp: [0.4, 0, 0.6, 1],
} as const;

type EasingKey = keyof typeof easingPoints;

const easingCss = Object.fromEntries(
  Object.entries(easingPoints).map(([key, points]) => [key, `cubic-bezier(${points.join(', ')})`]),
) as Record<EasingKey, string>;

export const motion = {
  /** Milliseconds — for CSS transitions and the MUI theme. */
  duration: durationMs,
  /** Seconds — for the Motion library's `transition.duration`. */
  durationS: durationSeconds,
  /** `cubic-bezier(...)` strings — for CSS. */
  easing: easingCss,
  /** `[x1, y1, x2, y2]` tuples — for Motion's `transition.ease`. */
  ease: easingPoints,

  /**
   * Ready-made Motion transitions, so components never hand-roll timings.
   * Import these instead of writing `{ duration: 0.2 }`, which the lint rule
   * rejects.
   */
  transition: {
    fade: { duration: durationSeconds.medium, ease: easingPoints.standard },
    enter: { duration: durationSeconds.medium, ease: easingPoints.decelerate },
    exit: { duration: durationSeconds.fast, ease: easingPoints.accelerate },
    drawer: { duration: durationSeconds.slow, ease: easingPoints.sharp },
    /** A soft spring for hover lifts and reorders. */
    spring: { type: 'spring', stiffness: 320, damping: 30, mass: 0.8 },
  },

  /**
   * Route transition variants shared by every page, so navigation feels like
   * one app rather than fifteen independently animated screens.
   */
  variants: {
    page: {
      initial: { opacity: 0, y: 8 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -4 },
    },
    listItem: {
      initial: { opacity: 0, y: 12 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0 },
    },
  },

  /**
   * Per-item delay for staggered list entrances, in seconds. Capped so a
   * 200-assignment course does not take 40 seconds to finish animating in.
   */
  stagger: 0.03,
  staggerMaxItems: 12,
} as const;

/* ------------------------------------------------------------------ */
/* Opacity + focus                                                     */
/* ------------------------------------------------------------------ */

export const opacity = {
  disabled: 0.38,
  hover: 0.04,
  selected: 0.08,
  focus: 0.12,
  pressed: 0.12,
  scrim: 0.6,
} as const;

/* ------------------------------------------------------------------ */
/* Public token object                                                 */
/* ------------------------------------------------------------------ */

/**
 * Everything above, in one importable object.
 *
 * ```ts
 * import { tokens } from '@/theme/tokens';
 * <Box sx={{ borderRadius: `${tokens.radius.md}px` }} />
 * ```
 *
 * In practice, prefer the MUI theme (`theme.palette.*`, `theme.spacing()`,
 * `theme.shape.borderRadius`) inside components — it is built from these
 * tokens, so it stays in sync automatically and handles light/dark for you.
 * Reach for `tokens` directly only for values the MUI theme has no slot for,
 * such as motion variants and course-card geometry.
 */
export const tokens = {
  color: { light: lightColors, dark: darkColors },
  ref,
  radius,
  space,
  spacingUnit,
  size,
  breakpoints,
  zIndex,
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  letterSpacing,
  shadow,
  shadowDark,
  motion,
  opacity,
} as const;

export type Tokens = typeof tokens;
export type ThemeMode = 'light' | 'dark';
